// sceneLayerAsset.ts — prepares the static scene 3DGS for upload.
//
// Policy: a .spz is uploaded verbatim (the author already chose their encoding
// parameters), while a .ply is transcoded to SH degree 0 .spz off the main
// thread. The uploader otherwise stores what it is given, and a raw DualGS-style
// .ply is ~104 B/splat against ~16 B/splat for degree-0 .spz — a 6.4x download
// for identical output, since the viewer strips SH at load time anyway.
import { SpzWorkerPool } from './spzWorkerPool'

export interface PreparedSceneAsset {
  /** Bytes to PUT. */
  body: BlobPart
  /** Extension for the R2 key: models/{uploadId}/static/scene.<ext> */
  ext: 'spz' | 'ply'
  /** True when we transcoded, so callers can report it. */
  transcoded: boolean
  originalBytes: number
  uploadBytes: number
}

/** Opacity floor applied while transcoding. Matches the default used by
 *  frontend/scripts/compress-to-spz.js; splats below it are effectively
 *  invisible but still cost a sort key on every frame swap. */
const OPACITY_THRESHOLD = 0.02

export function sceneFileExt(name: string): 'spz' | 'ply' | null {
  const lower = name.toLowerCase()
  if (lower.endsWith('.spz')) return 'spz'
  if (lower.endsWith('.ply')) return 'ply'
  return null
}

/**
 * Prepare a chosen scene file for upload.
 *
 * `.spz` passes through untouched. `.ply` is transcoded to SH degree 0 `.spz`
 * in a worker; if that fails for any reason we fall back to uploading the .ply
 * as-is rather than failing the upload — the viewer renders it correctly, just
 * larger.
 */
export async function prepareSceneAsset(
  file: File,
  onStatus?: (message: string) => void,
): Promise<PreparedSceneAsset> {
  const ext = sceneFileExt(file.name)
  if (ext === null) throw new Error('Scene layer must be a .spz or .ply file')

  if (ext === 'spz') {
    return { body: file, ext: 'spz', transcoded: false, originalBytes: file.size, uploadBytes: file.size }
  }

  onStatus?.('Converting scene to .spz…')
  const pool = new SpzWorkerPool(1)
  try {
    const plyBytes = new Uint8Array(await file.arrayBuffer())
    const spz = await pool.transcode(plyBytes, {
      name: file.name,
      // maxSh 0 is the point of this conversion: Spark re-runs the whole GPU
      // generate pass for any mesh carrying extra.sh1 on every camera move.
      transcode: { maxSh: 0, opacityThreshold: OPACITY_THRESHOLD },
    })
    return {
      body: spz as BlobPart,
      ext: 'spz',
      transcoded: true,
      originalBytes: file.size,
      uploadBytes: spz.byteLength,
    }
  } catch (err) {
    console.warn('[sceneLayer] .ply → .spz transcode failed, uploading the .ply as-is:', err)
    onStatus?.('Conversion failed — uploading the .ply as-is')
    return { body: file, ext: 'ply', transcoded: false, originalBytes: file.size, uploadBytes: file.size }
  } finally {
    pool.dispose()
  }
}
